import {
  tradeCustomPeriodValues,
  TradeCustomPeriod,
  tradeMonthPeriods,
  tradeQuarterAndSeasonalPeriods,
  dateColumns,
  ElecNominationEntry,
} from "@/constants/tradeOptions"
import dayjs, { Dayjs } from "dayjs"
import {
  ITradesElecData,
  ITradesElecHistoryData,
} from "@/models/operations/tradingData/elecData.types"
import {
  ITradesGasData,
  ITradesGasHistoryData,
} from "@/models/operations/tradingData/gasData.types"
import { formatDate } from "@/helpers/dateHelper"
import { groupBy } from "lodash"
import {
  HalfHourlyValue,
  IElecNominationsData,
} from "@/models/operations/tradingData/elecNominationsData.types"
import {
  FilterType,
  SoDataTableFilters,
  SoDataTableItem,
} from "@/types/components/SoDataTable"
import isBetween from "dayjs/plugin/isBetween"
dayjs.extend(isBetween)

export function getDateRangeForPeriod(period: string, year: string | null) {
  if (isMonthPeriod(period)) {
    const monthPeriod = `${period} ${year}`

    return [
      dayjs(monthPeriod).toDate(),
      dayjs(monthPeriod).endOf("month").startOf("day").toDate(),
    ]
  } else if (isQuarterOrSeasonalPeriod(period)) {
    const { start, end, getEndYearForPeriod } =
      tradeQuarterAndSeasonalPeriods[period] || {}
    const startOfPeriod = `${start} ${year}`
    const endOfPeriod = `${end} ${getEndYearForPeriod(year)}`

    return [
      dayjs(startOfPeriod).toDate(),
      dayjs(endOfPeriod).endOf("month").startOf("day").toDate(),
    ]
  } else if (isCustomPeriod(period)) {
    return getDatesByCustomPeriod(period as TradeCustomPeriod)
  } else return null
}

function getDatesByCustomPeriod(period: TradeCustomPeriod) {
  switch (period) {
    case "WD": {
      const currentDate = dayjs().startOf("day").toDate()
      return [currentDate, currentDate]
    }
    case "WEND": {
      return [
        dayjs().day(6).startOf("day").toDate(),
        dayjs().add(1, "week").day(0).startOf("day").toDate(),
      ]
    }
    case "DA": {
      let dayAhead: Date
      if ([5, 6, 0].includes(dayjs().day())) {
        dayAhead = dayjs().add(1, "week").day(1).startOf("day").toDate()
      } else dayAhead = dayjs().add(1, "days").startOf("day").toDate()

      return [dayAhead, dayAhead]
    }
    case "WDNW": {
      return [
        dayjs().add(1, "week").day(1).startOf("day").toDate(),
        dayjs().add(1, "week").day(5).startOf("day").toDate(),
      ]
    }
    case "BOW": {
      return [5, 6, 0].includes(dayjs().day())
        ? null
        : [
            dayjs().add(1, "day").startOf("day").toDate(),
            dayjs().day(5).startOf("day").toDate(),
          ]
    }
    case "BOM": {
      const startingDate =
        dayjs().day() == 5
          ? dayjs().add(1, "week").day(1)
          : dayjs().add(2, "day")

      return [
        startingDate.startOf("day").toDate(),
        startingDate.endOf("month").startOf("day").toDate(),
      ]
    }
    default:
      return null
  }
}

export function isCustomPeriod(period: string) {
  return tradeCustomPeriodValues.includes(period as TradeCustomPeriod)
}

// Each slot represents a half hour in a day
// eg. slots 47, 48 represent first and second half of hour 24
// There are 2 special cases for long and short day, when clock changes
const SLOT_47 = 47
const SLOT_48 = 48
const SLOT_LONG_DAY_LAST_HOUR_FIRST_HALF = 49
const SLOT_LONG_DAY_LAST_HOUR_SECOND_HALF = 50
const SLOT_SHORT_DAY_LAST_HOUR_FIRST_HALF = 45
const SLOT_SHORT_DAY_LAST_HOUR_SECOND_HALF = 46

const SHORT_DAY_TOTAL_NUMBER_OF_HALF_HOURS = 46
const LONG_DAY_TOTAL_NUMBER_OF_HALF_HOURS = 50
const SHORT_DAY_NUMBER_OF_HALF_HOUR_ENTRIES = 44

// Slots need to be moved to the hour ahead or behind depending on the case
// eg slots 49/50 (hour 25 on long day) move to 47/48 (hour 24)
const MOVE_SLOTS_ONE_HOUR_AHEAD = 2
const MOVE_SLOTS_ONE_HOUR_BEHIND = -2

function getSlotsToMove(
  dayEntries: ElecNominationEntry[],
  nextDayEntriesCount: number,
) {
  // case when moving to summer time
  if (dayEntries.length == SHORT_DAY_TOTAL_NUMBER_OF_HALF_HOURS) {
    if (nextDayEntriesCount == SHORT_DAY_TOTAL_NUMBER_OF_HALF_HOURS) {
      return []
    } else {
      return [
        SLOT_SHORT_DAY_LAST_HOUR_FIRST_HALF,
        SLOT_SHORT_DAY_LAST_HOUR_SECOND_HALF,
      ]
    }
    // case when moving to winter time
  } else if (dayEntries.length == LONG_DAY_TOTAL_NUMBER_OF_HALF_HOURS) {
    return [
      SLOT_LONG_DAY_LAST_HOUR_FIRST_HALF,
      SLOT_LONG_DAY_LAST_HOUR_SECOND_HALF,
    ]
    // normal case
  } else {
    return [SLOT_47, SLOT_48]
  }
}

export function processElecNominationsData(
  elecNominations: Array<ElecNominationEntry>,
) {
  const groupedNominations = groupBy(elecNominations, "Date")
  const sortedKeys = Object.keys(groupedNominations).sort(
    (a: string, b: string) => (new Date(a) > new Date(b) ? -1 : 1),
  )

  sortedKeys.forEach((dateKey) => {
    const dayEntries = groupedNominations[dateKey]
    const currentDay = dayEntries[0].Date
    const nextDay = new Date(currentDay)
    nextDay.setDate(nextDay.getDate() + 1)

    // last 2 entries of a day need to be moved to the next day
    // they depend on whether the switch to summer or winter time happens
    const nextDayEntries = groupedNominations[nextDay.toString()]
    if (!nextDayEntries) {
      return
    }
    const nextDayEntriesCount = nextDayEntries.length
    // there might be only 2 entries for a day (with slots 47, 48). These mean hour 47, 48 for the next day
    if (dayEntries.length == 2) {
      groupedNominations[nextDay.toString()].push(
        ...dayEntries.map((entry) => {
          // There are only 44 half hours entries for a short day => the 2 slots move to 45/46 instead of the normal 47/48
          const slotsDiff =
            nextDayEntriesCount == SHORT_DAY_NUMBER_OF_HALF_HOUR_ENTRIES
              ? MOVE_SLOTS_ONE_HOUR_BEHIND
              : 0
          return {
            Date: nextDay,
            Name: entry.Name,
            Volume: entry.Volume,
            SP: entry.SP + slotsDiff,
          } as ElecNominationEntry
        }),
      )
      delete groupedNominations[dateKey]
    } else if (groupedNominations[nextDay.toString()]) {
      const slotsToMove = getSlotsToMove(dayEntries, nextDayEntriesCount)
      const entriesToMove = dayEntries.filter((entry) =>
        slotsToMove.includes(entry.SP),
      )
      groupedNominations[nextDay.toString()].push(
        ...entriesToMove.map((entry) => {
          let slotsDiff = 0
          // slots 49/50 move to 47,48 in the next day
          if (slotsToMove.includes(SLOT_LONG_DAY_LAST_HOUR_FIRST_HALF))
            slotsDiff = MOVE_SLOTS_ONE_HOUR_BEHIND
          // slots 45/46 move to 47,48 in the next day
          else if (slotsToMove.includes(SLOT_SHORT_DAY_LAST_HOUR_FIRST_HALF))
            slotsDiff = MOVE_SLOTS_ONE_HOUR_AHEAD

          return {
            Date: nextDay,
            Name: entry.Name,
            Volume: entry.Volume,
            SP: entry.SP + slotsDiff,
          } as ElecNominationEntry
        }),
      )
      groupedNominations[dateKey] = dayEntries.filter(
        (entry) => !entriesToMove.includes(entry),
      )
      // for winter time changes, move slots 47/48 to 49/50, on current day
      if (groupedNominations[dateKey].length == 48) {
        groupedNominations[dateKey] = groupedNominations[dateKey].map(
          (entry) => {
            if (entry.SP == SLOT_47 || entry.SP == SLOT_48) {
              return {
                Date: entry.Date,
                Name: entry.Name,
                Volume: entry.Volume,
                SP: entry.SP + MOVE_SLOTS_ONE_HOUR_AHEAD,
              }
            } else {
              return entry
            }
          },
        )
      }
    }
  })

  return groupedNominations
}

export function getHalfHourlyVolume(
  data: IElecNominationsData,
  slots: Array<number>,
) {
  return data.halfHourlyValues
    .filter((halfHourlyValue: HalfHourlyValue) =>
      slots.includes(halfHourlyValue.timeSlot),
    )
    .map((halfHourlyValue: HalfHourlyValue) => halfHourlyValue.volume)
    .reduce((sum, current) => sum + current, 0)
}

function isMonthPeriod(period: string) {
  return tradeMonthPeriods.includes(period)
}

function isQuarterOrSeasonalPeriod(period: string) {
  return Object.keys(tradeQuarterAndSeasonalPeriods).includes(period)
}

export const getHistoryForElecTrade = (
  elecTrade: ITradesElecData,
  history?: ITradesElecHistoryData[],
) => {
  return history?.filter(
    (historyTrade) => historyTrade.elecTradeId === elecTrade.id,
  )
}

export const getHistoryForGasTrade = (
  gasTrade: ITradesGasData,
  history?: ITradesGasHistoryData[],
) => {
  return history?.filter(
    (historyTrade) => historyTrade.gasTradeId === gasTrade.id,
  )
}

export const parseHistoryData = (
  column: string,
  historyItem: ITradesGasHistoryData | ITradesElecHistoryData,
) => {
  if (historyItem[column]) {
    return dateColumns.includes(column)
      ? formatDate(historyItem[column])
      : historyItem[column]
  } else return "-"
}

export function filterTradesNumberColumns(
  filters: SoDataTableFilters,
  item: SoDataTableItem,
  columnName: string,
) {
  const filter = filters[columnName]
  if (filter.type !== FilterType.NUMBER_RANGE) return true
  if (!filter.value?.min || !filter.value?.max) return true
  return (
    (item[columnName] as number) >= filter.value.min &&
    (item[columnName] as number) <= filter.value.max
  )
}

export function filterTradesStringColumns(
  filters: SoDataTableFilters,
  item: SoDataTableItem,
  columnName: string,
) {
  const filter = filters[columnName]
  if (filter.type !== FilterType.CHECKBOX) return true
  if (!filter.value?.length) return true
  return filter.value.includes(item[columnName] as string)
}

export function filterTradesDatesColumns(
  filters: SoDataTableFilters,
  item: SoDataTableItem,
  columnName: string,
) {
  const filter = filters[columnName]
  if (filter.type !== FilterType.DATE_RANGE) return true
  if (!filter.value?.from || !filter.value?.to) return true
  return (
    item[columnName] != null &&
    (item[columnName] as Dayjs).isBetween(
      filter.value.from,
      filter.value.to,
      "day",
      "[]",
    )
  )
}
